import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from 'reactstrap';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'slices';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import BreadCrumb from 'Components/Common/BreadCrumb';
import IconActionButton from 'Components/pos2u/Buttons/IconActionButton';
import { FaListUl, FaRegPenToSquare } from 'react-icons/fa6';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomerSearchForm from './CustomerSearchForm';
import useWindowSize from 'Components/Hooks/WindowSizeHook';
import {
  CustomerListActionButtons,
  customerTableColumnConfig,
  getCustomerColumnWidth,
  RESPONSE_MESSAGE,
} from 'pages/Customers/CustomerConstants';
import { useTranslation } from 'react-i18next';
import { CustomerLightFromAPI } from 'types/customer/customerTypes';
import {
  setActiveCustomerId,
  setActivePointsHistoryIndex,
  setAdjustPointsForm,
  setAdjustPointsModalOpen,
  setCurrentPage,
  setCustomerForm,
  setCustomerModalOpen,
  setEntriesPerPage,
  setInputPageValue,
  resetCustomerForm,
  setPageFiltersBranch,
  setPointsHistoryInfoValues,
  setPointsHistoryModalOpen,
} from 'slices/customer/reducer';
import { useDispatch } from 'react-redux';
import CustomerModal from 'pages/Customers/CustomerManagementModals/CustomerModal';
import AdjustPointsModal from './AdjustPointsModal';
import {
  deleteBulkCustomers,
  getCustomerById,
  getCustomerPointsById,
  getCustomers,
  getExportCustomers,
  postCustomersSimple,
  getSearchCustomerPaginationIdsFromES,
  CustomerESSearchParams,
} from 'services/customerService';
import { transformArrayToStr } from 'utils/common/handleSelect';
import MainAddButton from 'Components/pos2u/Buttons/MainAddButton';
import DeleteModal from 'Components/Common/DeleteModal';
import { MdStars } from 'react-icons/md';
import TableWrapper from 'Components/pos2u/TableWrapper';
import {
  mapApiAdjustPointsForm,
  mapApiToCustomerForm,
} from 'utils/mappers/customerMapper';
import CustomerPointsHistoryModal from './CustomerPointsHistoryModal';
import ExportButton from './CustomerExport/ExportButton';
import CustomerExportSelectModal from './CustomerExport/CustomerExportSelectModal';
import { EXPORT_LIMIT, RETRIEVE_LIMIT } from './CustomerExport/constants';
import { formatNumberWithCommas } from 'utils/maths/Str2Num';
import { ExportRange } from 'types/common/Range';
import LoaderModal from 'Components/pos2u/Modals/LoaderModal';
import { setCustomersExportFilters } from 'slices/exportData/reducer';
import { exportCustomers } from 'Components/pos2u/Excel/exportCustomers';
import ModalBackdropError from 'Components/pos2u/ModalBackdropError';
import MainDeleteButton from 'Components/pos2u/Buttons/MainDeleteButton';

const CustomersPage = () => {
  const DevState = true;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const columnHelper = createColumnHelper<CustomerLightFromAPI>();

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  // const rs = getCustomerColumnWidth(isMobile, 'id', 'maxWidth');
  // console.log(`rs: ${rs}`);

  const { layoutModeType } = useSelector(
    (state: RootState) => ({
      layoutModeType: state.Layout.layoutModeType,
      // branches: state.Info.branches,
    }),
    shallowEqual
  );
  const isDarkMode = layoutModeType === 'dark';

  const customerState = useSelector((state: RootState) => state.Customer);
  const { PageFilters, PageDetails } = customerState;

  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded, user } = infoState;

  const { StoresTextMap } = useSelector(
    (state: RootState) => ({
      StoresTextMap: state.ExportData.StoresTextMap,
    }),
    shallowEqual
  );

  const productFormState = useSelector((state: RootState) => state.Product);
  const { group_price } = productFormState.form.pageConfig;

  const [exportModal, setExportModal] = useState<boolean>(false);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoaderModalOpen, setIsLoaderModalOpen] = useState(false);
  const [loaderError, setLoaderError] = useState('');

  // filters + pagination (TableContainer expects 0-based currentPage)
  // const [filters, setFilters] = useState<CustomerSearchFilters>(defaultFilters);

  // data

  const permissionsDetail: string[] = Array.isArray(user?.permissions_detail)
    ? user!.permissions_detail
    : [];

  const closeAddAndEditCustomers = permissionsDetail.includes(
    'customers_close_add_edit_del'
  );
  const availableBranchAccess = permissionsDetail.includes(
    'customers_only_available_branch_access'
  );

  const [customersList, setCustomersList] = useState<CustomerLightFromAPI[]>(
    []
  );
  const [customersTotal, setCustomersTotal] = useState<number>(0);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [latestSearchPersonIds, setLatestSearchPersonIds] = useState<
    string[] | null
  >(null);

  const totalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(customersTotal / Math.max(1, PageDetails.entriesPerPage))
      ),
    [customersTotal, PageDetails.entriesPerPage]
  );

  const fetchCustomerById = useCallback(async (id: number) => {
    try {
      const rs = await getCustomerById(id);
      if (rs.message === 'OK' && rs.error_code === 0) {
        return rs.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch Customer By Id:', error);
      return null;
    }
  }, []);

  const fetchCustomerPointsById = useCallback(async (id: number) => {
    try {
      const rs = await getCustomerPointsById(id);
      if (rs.message === 'OK' && rs.error_code === 0) {
        return rs.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch Customer Points By Id:', error);
      return null;
    }
  }, []);

  const refreshCustomerPointsInTable = useCallback(
    async (id: number) => {
      const customerPoints = await fetchCustomerPointsById(id);
      if (!customerPoints) {
        return;
      }

      setCustomersList((prevCustomers) =>
        prevCustomers.map((customer) =>
          customer.id === id
            ? {
                ...customer,
                point_balance: customerPoints.point_balance,
              }
            : customer
        )
      );
    },
    [fetchCustomerPointsById]
  );

  const fetchExportCustomers = useCallback(
    async (exportRange: ExportRange) => {
      setExportModal(false);
      const start = parseInt(exportRange.start);
      const end = parseInt(exportRange.end);
      const limit = RETRIEVE_LIMIT;

      if (!merchant?.id) return;
      if (PageFilters.branch.length === 0) return;

      const exportDataReqAmount = end - start + 1;
      const totalFetchAmount = Math.min(exportDataReqAmount, customersTotal);

      if (totalFetchAmount > EXPORT_LIMIT) {
        setErrorMsg(`Cannot export more than ${EXPORT_LIMIT} rows at once`);
        setIsError(true);
        return;
      }

      setIsLoaderModalOpen(true);

      try {
        const allData: any[] = [];

        for (let offset = 0; offset < totalFetchAmount; offset += limit) {
          const sliceStart = start + offset;
          let sliceEnd = sliceStart + limit - 1;

          // Clamp to user-defined end and total fetchable data
          sliceEnd = Math.min(sliceEnd, end, start + totalFetchAmount - 1);

          const rs = await getExportCustomers(
            // `[${merchant.id}]`,
            PageFilters.branch && PageFilters.branch.length === 0
              ? `[${merchant.id}]`
              : `[${transformArrayToStr(PageFilters.branch)}]`,
            PageFilters.accountNumber,
            PageFilters.phoneNumber,
            PageFilters.email,
            PageFilters.name,
            sliceStart,
            sliceEnd,
            PageDetails.currentPage + 1,
            PageDetails.entriesPerPage
          );

          if (rs?.message === 'OK' && rs.error_code === 0) {
            allData.push(...rs.data.customers);
          } else {
            console.warn(`Fetch failed for range ${sliceStart}-${sliceEnd}`);
          }
        }

        await exportCustomers(t, allData, StoresTextMap, group_price);

        setIsLoaderModalOpen(false);
      } catch (error) {
        console.error('Failed to fetch all products:', error);
        setErrorMsg(`Export failed: ${error}`);
        setIsLoaderModalOpen(false);
        setIsError(true);
        setLoaderError(`Export failed: ${error}`);
      }
    },
    [
      PageFilters.branch,
      PageFilters.accountNumber,
      PageFilters.phoneNumber,
      PageFilters.email,
      PageFilters.name,
      StoresTextMap,
      group_price,
      PageDetails.currentPage,
      PageDetails.entriesPerPage,
      merchant.id,
      t,
      customersTotal, // ← don’t forget this in dependencies
    ]
  );

  // selection
  // const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // const [isCheckAll, setIsCheckAll] = useState<boolean>(false);

  // const checkedAll = useCallback(() => {
  //   const updated = !isCheckAll;
  //   setIsCheckAll(updated);
  //   setSelectedIds(updated ? customersList.map((r) => r.id) : []);
  // }, [isCheckAll, customersList]);

  // const handleRowCheckboxChange = (id: number) => {
  //   setSelectedIds((prev) =>
  //     prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  //   );
  // };

  const [deleteModalMulti, setDeleteModalMulti] = useState<boolean>(false);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState<
    number[]
  >([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] =
    useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);

  const deleteMultiple = async () => {
    const checkall = document.getElementById('checkBoxAll') as HTMLInputElement;

    if (selectedCheckBoxDelete && selectedCheckBoxDelete.length > 0) {
      const response: any = await deleteBulkCustomers(selectedCheckBoxDelete);
      const { message, error_code } = response;
      if (message === 'OK' && error_code === 0) {
        toast.success(t(RESPONSE_MESSAGE.DELETE_SUCCESS), { autoClose: 3000 });
        fetchAllCustomers();
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    }
    setSelectedCheckBoxDelete([]);
    setIsCheckAll(false);
    setIsMultiDeleteButton(false);
    if (checkall) {
      checkall.checked = false;
    }
  };

  const checkedAll = useCallback(() => {
    const updatedCheckAllState = !isCheckAll;
    setIsCheckAll(updatedCheckAllState);
    const ele =
      document.querySelectorAll<HTMLInputElement>('.customerCheckBox');

    const tmp: number[] = [];
    if (updatedCheckAllState) {
      ele.forEach((ele) => {
        ele.checked = true;
        tmp.push(parseInt(ele.value, 10));
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    const CheckedEle = document.querySelectorAll('.customerCheckBox:checked');
    if (CheckedEle.length > 0) {
      setIsMultiDeleteButton(true);
    } else {
      setIsMultiDeleteButton(false);
    }

    setSelectedCheckBoxDelete(tmp);
  }, [isCheckAll]);

  const handleCheckboxChange = (id: number) => {
    const ele = document.querySelectorAll('.customerCheckBox:checked');
    if (ele.length > 0) {
      setIsMultiDeleteButton(true);
    } else {
      setIsMultiDeleteButton(false);
    }

    setSelectedCheckBoxDelete((prevSelected: number[]) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId: number) => selectedId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const Columns: ColumnDef<CustomerLightFromAPI, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: '#',
        header: () => (
          <div className="text-center">
            <input
              type="checkbox"
              id="checkBoxAll"
              className="form-check-input"
              onChange={() => checkedAll()}
              checked={isCheckAll}
            />
          </div>
        ),
        cell: (cell) => {
          const customerId = cell.row.original.id;
          return (
            <input
              type="checkbox"
              className="customerCheckBox form-check-input"
              value={customerId}
              data-testid={`checkbox-customer-${customerId}`}
              checked={selectedCheckBoxDelete.includes(customerId)}
              onChange={() => handleCheckboxChange(customerId)}
            />
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('account_number', {
        header: () => {
          return (
            // <div className="text-center">
            //   {createSortableHeader(
            //     t(customerTableColumnConfig.account_number.label),
            //     column,
            //     'd-flex align-items-center',
            //     {
            //       // fontSize: '0.8125rem',
            //       whiteSpace: 'pre-wrap',
            //       wordBreak: 'break-word',
            //     }
            //   )}
            // </div>
            <div
              className="text-center"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {t(customerTableColumnConfig.account_number.label)}
            </div>
          );
        },
        cell: (cell) => (
          <div
            className={`${customerTableColumnConfig.account_number.textColumnStyle} px-0 mx-0`}
          >
            <div>{String(cell.getValue() ?? '')}</div>
          </div>
        ),
        meta: {
          cellStyle: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            alignContent: 'flex-start',
            minWidth: getCustomerColumnWidth(
              isMobile,
              'account_number',
              'minWidth'
            ),
            maxWidth: getCustomerColumnWidth(
              isMobile,
              'account_number',
              'maxWidth'
            ),
          },
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('first_name', {
        // header: ({ column }) => {
        //   return (
        //     <div className="text-center">
        //       {createSortableHeader(
        //         t(customerTableColumnConfig.name.label),
        //         column,
        //         'd-flex align-items-center',
        //         {
        //           // fontSize: '0.8125rem',
        //           whiteSpace: 'pre-wrap',
        //           wordBreak: 'break-word',
        //         }
        //       )}
        //     </div>
        //   );
        // },
        header: () => {
          return (
            <div
              className="text-center"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {t(customerTableColumnConfig.name.label)}
            </div>
          );
        },
        cell: (cell) => (
          <div
            className={`${customerTableColumnConfig.name.textColumnStyle} px-0 mx-0`}
          >
            <div>
              {!cell.row.original.first_name && !cell.row.original.last_name
                ? ''
                : cell.row.original.first_name}
            </div>
            {/* <div>{String(cell.row.original ?? '-')}</div> */}
          </div>
        ),
        meta: {
          cellStyle: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            alignContent: 'flex-start',
            minWidth: getCustomerColumnWidth(isMobile, 'name', 'minWidth'),
            maxWidth: getCustomerColumnWidth(isMobile, 'name', 'maxWidth'),
          },
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
      ...Object.entries(customerTableColumnConfig)
        .filter(
          ([key]) =>
            key !== 'id' &&
            key !== 'action' &&
            key !== 'account_number' &&
            key !== 'name'
        )
        .map(([key, config]) =>
          columnHelper.accessor((row: any) => row[key], {
            id: key,
            header: () => {
              if (key === 'comments') {
                return <div className="text-center">{t(config.label)}</div>;
              }
              return (
                // <div className="text-center">
                //   {createSortableHeader(
                //     t(config.label),
                //     column,
                //     'd-flex align-items-center',
                //     {
                //       // fontSize: '0.8125rem',
                //       whiteSpace: 'pre-wrap',
                //       wordBreak: 'break-word',
                //     }
                //   )}
                // </div>

                <div
                  className="text-center"
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {t(config.label)}
                </div>
              );
            },
            // header: ({ column }) => {
            //   if (key === 'comments') {
            //     return <div className="text-center">{t(config.label)}</div>;
            //   }
            //   return (
            //     <div className="text-center">
            //       {createSortableHeader(
            //         t(config.label),
            //         column,
            //         'd-flex align-items-center',
            //         {
            //           // fontSize: '0.8125rem',
            //           whiteSpace: 'pre-wrap',
            //           wordBreak: 'break-word',
            //         }
            //       )}
            //     </div>
            //   );
            // },
            cell: (cell) => {
              if (key === 'group_price') {
                const groupPriceId: number | null = cell.getValue();

                const matched = group_price.find(
                  (gp) => gp.id === groupPriceId
                );
                const displayValue = matched ? matched.name : '-';

                return (
                  <div className={`${config.textColumnStyle} px-0 mx-0`}>
                    <div>{displayValue}</div>
                  </div>
                );
              }
              return (
                <div className={`${config.textColumnStyle} px-0 mx-0`}>
                  <div>{String(cell.getValue() ?? '-')}</div>
                </div>
              );
            },
            meta: {
              cellStyle: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                alignContent: 'flex-start',
                minWidth: getCustomerColumnWidth(
                  isMobile,
                  key as keyof typeof customerTableColumnConfig,
                  'minWidth'
                ),
                maxWidth: getCustomerColumnWidth(
                  isMobile,
                  key as keyof typeof customerTableColumnConfig,
                  'maxWidth'
                ),
              },
            },
            enableColumnFilter: false,
            enableSorting: false,
          })
        ),
      columnHelper.display({
        id: 'action',
        header: () => <div className="text-center">{t('Action')}</div>,
        cell: ({ row }) => {
          const customer = row.original;
          const toggleId = `dd-menu-${customer.id}`;

          const handleMouseEnter = () => {
            const toggleButton = document.querySelector(
              `[data-toggle="${toggleId}"]`
            );
            if (toggleButton) (toggleButton as HTMLElement).click();
          };

          const handleMouseLeave = () => {
            const dropdownMenu = document.querySelector(
              `[data-dropdown-menu="${toggleId}"]`
            );
            if (dropdownMenu) {
              const toggleButton = document.querySelector(
                `[data-toggle="${toggleId}"]`
              );
              if (toggleButton && dropdownMenu.classList.contains('show')) {
                (toggleButton as HTMLElement).click();
              }
            }
          };
          return (
            <ul
              className="list-inline hstack gap-0 mb-0"
              key={`customer-${customer.id}`}
              onMouseLeave={handleMouseLeave}
            >
              <li className="list-inline-item">
                <IconActionButton
                  onClick={async () => {
                    dispatch(setActiveCustomerId(customer.id.toString()));
                    const rawValue = await fetchCustomerById(customer.id);
                    const formValue = mapApiToCustomerForm(rawValue);
                    dispatch(setCustomerForm(formValue));
                    dispatch(setCustomerModalOpen(true));
                  }}
                  icon={
                    <FaRegPenToSquare
                      fontSize={CustomerListActionButtons.icon}
                    />
                  }
                  title={t('Edit')}
                  height={CustomerListActionButtons.button.height}
                  width={CustomerListActionButtons.button.width}
                />
              </li>
              <li className="list-inline-item">
                <IconActionButton
                  onClick={async () => {
                    dispatch(setActiveCustomerId(customer.id.toString()));

                    const rawValue = await fetchCustomerPointsById(customer.id);
                    const formValue = mapApiAdjustPointsForm(rawValue);
                    dispatch(setAdjustPointsForm(formValue));
                    dispatch(setAdjustPointsModalOpen(true));

                    // toast.info(`Adjust points for ${customer.id}`);
                  }}
                  icon={<MdStars fontSize={CustomerListActionButtons.icon} />}
                  title={t('Adjust Points')}
                  height={CustomerListActionButtons.button.height}
                  width={CustomerListActionButtons.button.width}
                />
              </li>
              {DevState && (
                <li className="list-inline-item">
                  <ButtonGroup>
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="span"
                        data-toggle={toggleId}
                        onMouseEnter={handleMouseEnter}
                        style={{
                          cursor: 'pointer',
                          width: CustomerListActionButtons.button.width,
                          height: CustomerListActionButtons.button.height,
                        }}
                        className="btn btn-outline-success px-0 py-0 d-flex align-items-center justify-content-center"
                      >
                        <i
                          className="las las la-ellipsis-h"
                          style={{
                            fontSize: CustomerListActionButtons.icon,
                          }}
                        ></i>
                      </DropdownToggle>
                      <DropdownMenu
                        className="dropdownmenu-success"
                        data-dropdown-menu={toggleId}
                      >
                        <DropdownItem
                          id={`points-history-${customer.id}`}
                          className="text-steel-gray"
                          onMouseOver={(e) =>
                            e.currentTarget.classList.remove('text-steel-gray')
                          }
                          onMouseOut={(e) =>
                            e.currentTarget.classList.add('text-steel-gray')
                          }
                          onClick={async () => {
                            dispatch(
                              setActivePointsHistoryIndex(
                                customer.id.toString()
                              )
                            );

                            const rawValue = await fetchCustomerPointsById(
                              customer.id
                            );

                            dispatch(setPointsHistoryInfoValues(rawValue));
                            dispatch(setPointsHistoryModalOpen(true));
                          }}
                        >
                          <div title={t('Points History')}>
                            <FaListUl size={24} style={{ padding: '0.2rem' }} />{' '}
                            {t('Points History')}
                          </div>
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </ButtonGroup>
                </li>
              )}
              {/* <li className="list-inline-item">
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="a"
                    role="button"
                    className="btn btn-outline-secondary p-1"
                  >
                    •••
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-md">
                    <DropdownItem
                      onClick={() => {
                        dispatch(setActiveCustomerId(customer.account_number));
                        dispatch(setAdjustPointsModalOpen(true));

                        toast.info(`Adjust points for ${customer.id}`);
                      }}
                    >
                      Adjust Points
                    </DropdownItem>

                  </DropdownMenu>
                </UncontrolledDropdown>
              </li> */}
            </ul>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [
      checkedAll,
      isCheckAll,
      selectedCheckBoxDelete,
      group_price,
      t,
      isMobile,
      columnHelper,
      dispatch,
      fetchCustomerById,
      fetchCustomerPointsById,
      DevState,
    ]
  );

  const fetchAllCustomers = useCallback(async () => {
    if (!merchant?.id) return;
    if (PageFilters.branch.length === 0) return;
    setIsLoadSuccess(false);
    try {
      if (latestSearchPersonIds !== null) {
        if (latestSearchPersonIds.length === 0) {
          setCustomersTotal(0);
          setCustomersList([]);
          setIsLoadSuccess(true);
          return;
        }

        const sanitizedSearchPersonIds = latestSearchPersonIds
          .map((personId) => Number(personId))
          .filter((personId) => Number.isFinite(personId));
        const selectedStoreIds = PageFilters.branch
          .map((branchId) => Number(branchId))
          .filter((branchId) => Number.isFinite(branchId));

        if (sanitizedSearchPersonIds.length === 1) {
          const simpleResponse = await postCustomersSimple({
            store_ids: selectedStoreIds,
            page: 1,
            limit: PageDetails.entriesPerPage,
            person_ids: sanitizedSearchPersonIds,
          });

          if (
            simpleResponse?.message === 'OK' &&
            simpleResponse.error_code === 0
          ) {
            setCustomersTotal(simpleResponse.data?.total ?? 0);
            setCustomersList(simpleResponse.data?.customers ?? []);
            setIsLoadSuccess(true);
            return;
          }

          setCustomersTotal(0);
          setCustomersList([]);
          setIsLoadSuccess(true);
          return;
        }

        const customerESParams: CustomerESSearchParams = {
          ...(PageFilters.name.trim() ? { name: PageFilters.name.trim() } : {}),
          ...(PageFilters.phoneNumber.trim()
            ? { phone: PageFilters.phoneNumber.trim() }
            : {}),
          ...(PageFilters.accountNumber.trim()
            ? { account_number: PageFilters.accountNumber.trim() }
            : {}),
          ...(PageFilters.email.trim()
            ? { email: PageFilters.email.trim() }
            : {}),
        };
        const searchIdsResponse = await getSearchCustomerPaginationIdsFromES(
          customerESParams,
          PageDetails.currentPage + 1,
          PageDetails.entriesPerPage
        );

        if (
          !searchIdsResponse ||
          searchIdsResponse.message !== 'OK' ||
          searchIdsResponse.error_code !== 0
        ) {
          setCustomersTotal(0);
          setCustomersList([]);
          setIsLoadSuccess(true);
          return;
        }

        const personIds = (searchIdsResponse.data?.limit_results?.list ?? [])
          .map((personId) => Number(personId))
          .filter((personId) => Number.isFinite(personId));

        if (personIds.length === 0) {
          setCustomersTotal(0);
          setCustomersList([]);
          setIsLoadSuccess(true);
          return;
        }

        const simpleResponse = await postCustomersSimple({
          store_ids: selectedStoreIds,
          page: 1,
          limit: PageDetails.entriesPerPage,
          person_ids: personIds,
        });

        if (
          simpleResponse?.message === 'OK' &&
          simpleResponse.error_code === 0
        ) {
          // total_amount = ยอดรวมทั้งหมดจาก server (limit_results.amount เป็นจำนวนเฉพาะในหน้านี้)
          setCustomersTotal(
            searchIdsResponse.data?.total_amount ??
              sanitizedSearchPersonIds.length
          );
          setCustomersList(simpleResponse.data?.customers ?? []);
          setIsLoadSuccess(true);
          return;
        }

        setCustomersTotal(0);
        setCustomersList([]);
        setIsLoadSuccess(true);
        return;
      }

      const response = await getCustomers(
        // `[${merchant.id}]`,
        PageFilters.branch && PageFilters.branch.length === 0
          ? `[${merchant.id}]`
          : `[${transformArrayToStr(PageFilters.branch)}]`,
        PageFilters.accountNumber,
        PageFilters.phoneNumber,
        PageFilters.email,
        PageFilters.name,
        PageDetails.currentPage + 1,
        PageDetails.entriesPerPage,
        'desc'
      );

      if (response) {
        const { message, error_code, data } = response;
        if (message === 'OK' && error_code === 0) {
          const { customers, total } = data;
          setCustomersTotal(total);
          setCustomersList(customers);

          // setIsNotFound(total === 0);
        } else {
          setCustomersTotal(0);
          setCustomersList([]);
        }
        setIsLoadSuccess(true);
      } else {
        console.error('No response data returned');
        setCustomersTotal(0);
        setCustomersList([]);
        setIsLoadSuccess(true);
      }
    } catch (error) {
      console.error('Failed to fetch category data:', error);
      setCustomersTotal(0);
      setCustomersList([]);
      setIsLoadSuccess(true);
    }
  }, [
    latestSearchPersonIds,
    PageDetails.currentPage,
    PageDetails.entriesPerPage,
    PageFilters,
    merchant,
  ]);

  useEffect(() => {
    if (user.store_id_login) {
      dispatch(setPageFiltersBranch([user.store_id_login.toString()]));
    }
  }, [user.store_id_login, dispatch]);

  useEffect(() => {
    if (isMerchantLoaded && merchant.id && PageFilters.branch.length > 0) {
      fetchAllCustomers();
    }
  }, [
    fetchAllCustomers,
    isMerchantLoaded,
    merchant.id,
    PageFilters.branch.length,
  ]);

  useEffect(() => {
    if (customersList) {
      setIsLoadSuccess(true);
    } else {
      setIsLoadSuccess(false);
    }
  }, [customersList]);

  // const handleDeleteSelected = () => {
  //   if (selectedIds.length === 0) return;
  //   toast.info(
  //     `Delete ${selectedIds.length} selected customers (implement me)`
  //   );
  // };

  // const handleExportExcel = () => {
  //   toast.info('Export to Excel (implement me)');
  // };

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };

  // const handleSetEntriesPerPage = (value: number) => {
  //   setEntriesPerPage(value);
  //   setCurrentPage(0);
  //   setInputPageValue(1);
  // };

  // const breadcrumbItems = [
  //   { title: 'POS2U', link: '#' },
  //   { title: 'Customers', link: '#' },
  // ];
  document.title = t('Customer') + ' | POS2U';
  return (
    <div className="page-content">
      {/* <CustomerExportModal
        title={t('Customers')}
        message={t('MaximumExportLimit', {
          limit: formatNumberWithCommas(EXPORT_LIMIT, 0),
        })}
        show={exportModal}
        onConfirmClick={async (values: ExportRange) => {
          dispatch(setCustomersExportFilters(values));
          await fetchExportCustomers(values);
        }}
        onCloseClick={() => setExportModal(false)}
        totalData={customersTotal}
        cancelBtnMsg={t('Cancel')}
        confirmBtnMsg={t('Confirm')}
        type={1}
      /> */}
      <CustomerExportSelectModal
        title={t('Customers')}
        message={t('MaximumExportLimit', {
          limit: formatNumberWithCommas(EXPORT_LIMIT, 0),
        })}
        show={exportModal}
        onConfirmClick={async (values: ExportRange) => {
          dispatch(setCustomersExportFilters(values));
          await fetchExportCustomers(values);
        }}
        onCloseClick={() => setExportModal(false)}
        totalData={customersTotal}
        cancelBtnMsg={t('Cancel')}
        confirmBtnMsg={t('Confirm')}
        type={1}
      />
      <ModalBackdropError
        isError={isError}
        setIsError={setIsError}
        message={errorMsg}
        ButtonMsg="OK"
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
        recordId=""
        title={t('Delete Customer')}
        message={
          selectedCheckBoxDelete.length === 1
            ? t('SingleDeleteAlertMessage')
            : t('MultiDeleteAlertMessage', {
                items: selectedCheckBoxDelete.length,
              })
        }
        // annotation={
        //   selectedCheckBoxDelete.length === 1
        //     ? t('SingleDeleteProductAnnotation')
        //     : t('MultiDeleteProductAnnotation')
        // }
        cancelBtnMsg={t('Cancel')}
        confirmBtnMsg={t('Delete')}
      />
      <Container fluid>
        <BreadCrumb title={t('Customers')} pageTitle={t('Customers')} />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">{t('Customers List')}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {/* <button
                        type="button"
                        className="btn btn-soft-success add-btn"
                        id="add-product-btn"
                        onClick={() => {
                          history(`${productURI}/add`);
                        }}
                      >
          
                        <MdOutlineAdd fontSize={ProductListButtonIconSize} />
                        {t('Btn_CreateProduct')}
                      </button> */}
                      <MainAddButton
                        // to={`${CUSTOMER.LIST}/add`}
                        to={``}
                        className={`${
                          closeAddAndEditCustomers ? 'd-none' : ''
                        }`}
                        onClick={() => {
                          dispatch(resetCustomerForm());
                          dispatch(setActiveCustomerId(null));
                          dispatch(setCustomerModalOpen(true));
                        }}
                        label={t('Create Customer')}
                        id="add-customer-btn"
                        soft={true}
                      />{' '}
                      <ExportButton
                        _limitData={customersTotal}
                        toggleExportModal={setExportModal}
                      />
                      {/* <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{' '}
                        Export
                      </button>{' '} */}
                      {isMultiDeleteButton && (
                        // <button
                        //   className="btn btn-soft-danger"
                        //   data-testid="multi-delete-button"
                        //   onClick={() => setDeleteModalMulti(true)}
                        // >
                        //   {/* <i className="ri-delete-bin-2-line"></i> */}
                        //   <RiDeleteBin2Line
                        //     size={CustomerListActionButtons.icon}
                        //   />
                        // </button>
                        <MainDeleteButton
                          onClick={() => setDeleteModalMulti(true)}
                          iconSize={CustomerListActionButtons.icon}
                        />
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody>
                <CustomerSearchForm
                  isDarkMode={isDarkMode}
                  onSubmit={(_v, personIds) => {
                    setIsCheckAll(false);
                    setIsMultiDeleteButton(false);
                    setSelectedCheckBoxDelete([]);
                    setLatestSearchPersonIds(personIds ?? null);
                    dispatch(setCurrentPage(0));
                  }}
                  onSearchPersonIdsChange={setLatestSearchPersonIds}
                  selectedCheckBoxDelete={selectedCheckBoxDelete.length}
                />
                {/* {isLoadSuccess ? (
                  <TableContainerPOS2U
                    columns={Columns}
                    data={customersList}
                    isGlobalFilter={false}
                    isPromoQuery={false}
                    customPageSize={PageDetails.entriesPerPage}
                    divClass="table-responsive table-card mb-1 mt-0"
                    tableClass="align-middle table-nowrap"
                    theadClass={`table-${
                      isDarkMode ? 'dark' : 'light'
                    } text-muted text-uppercase`}
                    trClass=""
                    thClass=""
                    SearchPlaceholder="Search..."
                    selectedCheckBoxDelete={selectedCheckBoxDelete.length}
                    TotalData={customersTotal}
                    totalPages={totalPages}
                    currentPage={PageDetails.currentPage}
                    setEntriesPerPage={setEntriesPerPage}
                    handlePageChange={handlePageChange}
                    inputPageValue={PageDetails.inputPageValue}
                    setInputPageValue={setInputPageValue}
                  />
                ) : (
                  <Loader />
                )} */}
                <TableWrapper
                  columns={Columns}
                  data={customersList}
                  customPageSize={PageDetails.entriesPerPage}
                  divClass="table-responsive table-card mb-1 mt-0"
                  tableClass="align-middle table-nowrap"
                  theadClass={`table-${
                    isDarkMode ? 'dark' : 'light'
                  } text-muted text-uppercase`}
                  trClass=""
                  thClass=""
                  TotalData={customersTotal}
                  totalPages={totalPages}
                  currentPage={PageDetails.currentPage}
                  setEntriesPerPage={setEntriesPerPage}
                  handlePageChange={handlePageChange}
                  inputPageValue={PageDetails.inputPageValue}
                  setInputPageValue={setInputPageValue}
                  isTableLoadSuccess={isLoadSuccess}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <CustomerModal
        fetchCustomers={fetchAllCustomers}
        closeAddAndEditCustomers={closeAddAndEditCustomers}
        availableBranchAccess={availableBranchAccess}
      />
      <AdjustPointsModal
        fetchPoints={(personId: number) => {
          void refreshCustomerPointsInTable(personId);
        }}
      />
      <CustomerPointsHistoryModal />
      <LoaderModal
        isOpen={isLoaderModalOpen}
        error={loaderError}
        onClose={() => setIsLoaderModalOpen(false)}
      />
      <ToastContainer closeButton={false} limit={1} />
    </div>
  );
};

export default CustomersPage;
